import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { usePopupSide } from '@/lib/use-popup-side'

export interface DropdownOption<T extends string = string> {
  value: T
  label: string
  disabled?: boolean
}

interface DropdownProps<T extends string = string> {
  options: DropdownOption<T>[]
  value: T | null
  onChange: (value: T) => void
  placeholder?: string
  label?: string
  error?: string
  disabled?: boolean
  className?: string
  id?: string
  /**
   * Reserved for Phase 2d label-picker. Accepting it here keeps the call-site
   * stable when multi-select lands; flipping to true today is a no-op.
   */
  multiple?: false
  /**
   * Mount with the listbox already open and focused. Opt-in: callers that
   * render the dropdown as an always-visible form control (create / clone
   * modals, the recurrence editor) keep the closed default.
   */
  defaultOpen?: boolean
  /**
   * Called after Escape closes the listbox. For a host that treats the open
   * list AS the editor (a click-to-edit field) and so has to end the edit in
   * the same keypress, rather than be left editing behind a closed trigger.
   */
  onDismiss?: () => void
  /**
   * `rail` draws the trigger as an in-place editor of a card-detail rail row:
   * the same 36px, 8px-inset box the row's display control occupies, with the
   * strong "editing" border. The list is an absolutely positioned overlay in
   * both variants, so opening it never moves anything.
   */
  variant?: 'default' | 'rail'
}

export function Dropdown<T extends string = string>({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  disabled = false,
  className = '',
  id,
  defaultOpen = false,
  onDismiss,
  variant = 'default',
}: DropdownProps<T>) {
  const rail = variant === 'rail'
  const { t } = useTranslation('common')
  const resolvedPlaceholder = placeholder ?? t('dropdown.placeholder')
  const generatedId = useId()
  const triggerId = id ?? `dropdown-${generatedId}`
  const listboxId = `${triggerId}-listbox`

  const [open, setOpen] = useState(defaultOpen)
  const [activeIndex, setActiveIndex] = useState<number>(() => {
    const idx = options.findIndex((o) => o.value === value)
    return idx >= 0 ? idx : 0
  })

  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listboxRef = useRef<HTMLDivElement | null>(null)

  const side = usePopupSide(containerRef, open && rail, 248)
  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  )

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (options.length === 0) return
      setActiveIndex((current) => {
        let next = current
        for (let i = 0; i < options.length; i += 1) {
          next = (next + direction + options.length) % options.length
          if (!options[next]?.disabled) return next
        }
        return current
      })
    },
    [options],
  )

  const commit = useCallback(
    (index: number) => {
      const option = options[index]
      if (!option || option.disabled) return
      onChange(option.value)
      setOpen(false)
      triggerRef.current?.focus()
    },
    [onChange, options],
  )

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return
      const isOpener =
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      if (!isOpener) return
      event.preventDefault()
      if (!open) setOpen(true)
      if (event.key === 'ArrowDown') moveActive(1)
      else if (event.key === 'ArrowUp') moveActive(-1)
    },
    [disabled, moveActive, open],
  )

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveActive(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveActive(-1)
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        commit(activeIndex)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        // Escape closes the LISTBOX only. `preventDefault` alone is not
        // enough: the synthetic event keeps bubbling up the React tree to an
        // enclosing `<Modal>`'s dialog `onKeyDown`, which closes on Escape
        // without gating on `defaultPrevented` — so one keystroke inside an
        // open listbox discarded a half-filled form.
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
        onDismiss?.()
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setActiveIndex(options.length - 1)
      } else if (event.key === 'Tab') {
        setOpen(false)
      }
    },
    [activeIndex, commit, moveActive, onDismiss, options.length],
  )

  // Sync active index to current value when opening.
  useEffect(() => {
    if (!open) return
    const idx = options.findIndex((o) => o.value === value)
    if (idx >= 0) setActiveIndex(idx)
  }, [open, options, value])

  // Move keyboard focus into the listbox when it opens.
  useEffect(() => {
    if (open) {
      listboxRef.current?.focus()
    }
  }, [open])

  // Click-outside close.
  useEffect(() => {
    if (!open) return
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  const triggerBase =
    'flex w-full items-center justify-between rounded-md border bg-bg-elevated text-start text-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-bg-muted'
  const triggerSize = rail ? 'h-9 px-2 leading-5' : 'px-3 py-2 shadow-sm'
  const triggerState = rail
    ? error
      ? 'border-danger text-fg ring-1 ring-danger'
      : 'border-fg text-fg ring-1 ring-fg'
    : error
      ? 'border-danger text-fg focus:border-danger focus:ring-red-500'
      : 'border-border-strong text-fg focus:border-fg focus:ring-fg'

  return (
    <div
      className={`flex flex-col gap-1 ${rail ? '-mx-2 w-[calc(100%+1rem)]' : ''} ${className}`.trim()}
    >
      {label && (
        <label htmlFor={triggerId} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={triggerId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={handleTriggerKeyDown}
          className={`${triggerBase} ${triggerSize} ${triggerState}`}
        >
          <span className={selectedOption ? 'text-fg' : 'text-fg-subtle'}>
            {selectedOption ? selectedOption.label : resolvedPlaceholder}
          </span>
          <span aria-hidden="true" className="ms-2 text-fg-subtle">
            {open ? t('dropdown.expandedSuffix') : t('dropdown.collapsedSuffix')}
          </span>
        </button>
        {open && (
          <div
            ref={listboxRef}
            id={listboxId}
            // biome-ignore lint/a11y/useSemanticElements: ARIA listbox pattern (WAI-ARIA combobox spec); native <select> can't render this UI.
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              options[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined
            }
            onKeyDown={handleListKeyDown}
            className={`absolute start-0 end-0 z-40 max-h-60 overflow-auto rounded-md border border-border-strong bg-bg-elevated py-1 shadow-lg focus:outline-none ${
              rail && side === 'above' ? 'bottom-full mb-1' : 'mt-1'
            }`}
          >
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-fg-subtle">{t('dropdown.noOptions')}</div>
            )}
            {options.map((option, index) => {
              const isActive = index === activeIndex
              const isSelected = option.value === value
              return (
                // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the listbox; selection moves via aria-activedescendant, not roving tabindex.
                <div
                  key={option.value}
                  id={`${listboxId}-opt-${index}`}
                  // biome-ignore lint/a11y/useSemanticElements: ARIA option role on <div>; <option> only works inside <select>.
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    if (!option.disabled) commit(index)
                  }}
                  className={[
                    'cursor-pointer px-3 py-2 text-sm',
                    option.disabled
                      ? 'cursor-not-allowed text-fg-subtle'
                      : isActive
                        ? 'bg-bg-muted text-fg'
                        : 'text-fg',
                    isSelected ? 'font-medium' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {option.label}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}
